import { ColumnDef } from "@tanstack/react-table";
import { useMemo } from "react";

import { IPatientTreatment } from "../../../../@types/admin/AdminTreatment";
import { TableSorted } from "../../../../components/TableSorted";
import { usePatientTreatments } from "../../../../hooks/queries/admin/treatments";
import { getLongFormattedDate } from "../../../../utils/dateFormatter";
import {
  ErrorMessage,
  FiltersContainer,
  PanelContainer,
  PanelHeader,
  PanelTitle,
  SearchInput,
  StatusBadge,
} from "../AdminPage.styles";
import { TableContainer } from "../TableContainer";
import { TablePagination } from "../TablePagination";
import { TableLoadingState } from "../TableStates";
import { TableWrapper } from "../TableWrapper";
import { useMockPagination } from "../useMockPagination";

const SEARCH_FIELDS: (keyof IPatientTreatment)[] = [
  "id",
  "serialNumber",
  "treatmentNumber",
  "doseStatusDisplay",
  "time",
];

export interface IPatientTreatmentsPanelProps {
  /** Patient whose treatments to show. Undefined keeps the query idle. */
  patientId: number | undefined;
  title?: string;
  /** Rows per page. Defaults to the shared /admin page size. */
  pageSize?: number;
}

/**
 * Seconds as Retool's `valueOverride` rendered them ("12 seconds"), with a
 * dash for a missing reading so the column never shows a bare unit.
 */
function seconds(value: number | null | undefined): string {
  return value == null ? "-" : `${value} seconds`;
}

/** Standard litres per minute, Retool's `{{ item }} slm`. */
function slm(value: number | null | undefined): string {
  return value == null ? "-" : `${value} slm`;
}

/**
 * Embeddable list of one patient's nebulizer treatments — the portal port of
 * Retool's `modulePatientTreatments` global widget (EN-5156). Retool embedded
 * that module in the Patients and Archived Patients pages next to the
 * Treatments app itself, so it ships as a shared component: those tickets
 * (EN-5170, EN-5171) embed this rather than inlining another table.
 *
 *   <PatientTreatmentsPanel patientId={patient.id} />
 *
 * Behaviour kept from the module: the client-side search box, the
 * Complete/Incomplete status tag, and the per-treatment device metrics. The
 * module hid the metric columns behind Retool's column picker; the portal
 * shows the ones an operator actually reads and leaves the rest to the
 * Treatments export.
 */
export function PatientTreatmentsPanel({
  patientId,
  title = "Treatments",
  pageSize,
}: IPatientTreatmentsPanelProps) {
  const { treatments, isLoading, isError } = usePatientTreatments(patientId);

  const {
    items,
    allItems,
    pagination,
    searchTerm,
    setSearchTerm,
    setPage,
    setPageSize,
  } = useMockPagination({
    data: treatments,
    defaultPageSize: pageSize,
    searchFields: SEARCH_FIELDS,
  });

  const columns: ColumnDef<IPatientTreatment, string>[] = useMemo(
    () => [
      {
        accessorKey: "treatmentNumber",
        header: "Treatment number",
        cell: ({ row }) => <span>{row.original.treatmentNumber}</span>,
        enableSorting: true,
      },
      {
        accessorKey: "serialNumber",
        header: "Serial number",
        enableSorting: true,
      },
      {
        // Retool tagged this Complete (default) / Incomplete (red). The
        // accessor feeds both the badge and the sort — TableSorted skips a
        // cell renderer whose value is falsy.
        id: "doseStatusDisplay",
        header: "Status",
        accessorFn: (treatment) => treatment.doseStatusDisplay,
        cell: ({ row }) => (
          <StatusBadge
            status={
              row.original.doseStatusDisplay === "Complete"
                ? "active"
                : "inactive"
            }
          >
            {row.original.doseStatusDisplay}
          </StatusBadge>
        ),
        enableSorting: true,
      },
      {
        accessorKey: "time",
        header: "Time of treatment",
        cell: ({ row }) => (
          <span>{getLongFormattedDate(row.original.time, "-")}</span>
        ),
        enableSorting: true,
      },
      {
        id: "totalTreatmentTime",
        header: "Treatment duration",
        accessorFn: (treatment) => seconds(treatment.totalTreatmentTime),
        enableSorting: true,
      },
      {
        id: "totalTimeInhaling",
        header: "Total time inhaling",
        accessorFn: (treatment) => seconds(treatment.totalTimeInhaling),
        enableSorting: true,
      },
      {
        id: "meanPeakFlow",
        header: "Average peak flow rate",
        accessorFn: (treatment) => slm(treatment.meanPeakFlow),
        enableSorting: true,
      },
      {
        id: "chamberAgeDays",
        header: "Cartridge - time in use",
        accessorFn: (treatment) =>
          treatment.chamberAgeDays == null
            ? "-"
            : `${treatment.chamberAgeDays} days`,
        enableSorting: true,
      },
    ],
    []
  );

  return (
    <PanelContainer>
      <PanelHeader>
        <PanelTitle>{title}</PanelTitle>
      </PanelHeader>

      <FiltersContainer>
        <SearchInput
          type="text"
          placeholder="Search by treatment number, serial number, or status..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
      </FiltersContainer>

      {isError && (
        <ErrorMessage>
          Failed to load this patient&apos;s treatments. Try refreshing the
          page.
        </ErrorMessage>
      )}

      <TableContainer>
        <TableWrapper refreshKeys={[["admin-patient-treatments"]]}>
          <TableSorted
            columns={columns}
            data={items}
            emptyStateText={isLoading ? <TableLoadingState /> : "No rows found"}
          />
        </TableWrapper>
        <TablePagination
          exportCsv={{
            filename: "patient-treatments",
            columns,
            rows: allItems,
          }}
          pagination={pagination}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      </TableContainer>
    </PanelContainer>
  );
}
